/**
 * @flow
 */
import React from 'react';
import moment from 'moment';
import {Icon} from '@material-ui/core';
import Storage from './Storage';
import {PERM_COMPANY_ADMIN, PERM_SUPER_ADMIN} from '../types/types';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface ReportInfo {
  company: string;
  branch: string;
  name: string;
}

class Util {

  reportInfo: ReportInfo;

  constructor() {
    process.env.NODE_ENV !== 'production' && console.log('[Util] initialized.');
  }

  clone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  formatDate(timestamp: number, format: string = 'YYYY-MM-DD HH:mm:ss') {
    return timestamp ? moment(timestamp).format(format) : '';
  }

  formatActive(active: 'Y' | 'N') {
    return active === 'Y' ? <Icon color="primary" size={'small'}>check</Icon> : null;
  }

  formatTimer(time: number) {
    if(isNaN(time)) return '';
    const hh = (time / 100).toFixed(0);
    const mm = time % 100;
    return `${hh}:${mm < 10 ? '0'+mm : mm}`;
  }

  formatFirst(value: string) {
    return <span className={'pl-24'}>{value}</span>;
  }

  formatPermission(value: number) {
    switch(value) {
      case 0: return 'Super Admin';
      case 10: return 'Company Admin';
      case 11: return 'Branch Admin';
      case 12: return 'Group Admin';
      case 20: return 'User';
      default: return 'Unknown';
    }
  }

  formatActivityOnOff(value: string) {
    if(value === 'ON') {
      return <small className="border-radius-4 text-white px-8 py-2" style={{backgroundColor: '#2EA433'}}>{value}</small>;
    } else if(value === 'OFF') {
      return <small className="border-radius-4 text-white px-8 py-2" style={{backgroundColor: '#F57C00'}}>{value}</small>;
    } else {
      return <small className="border-radius-4 bg-error text-white px-8 py-2">{'NONE'}</small>;
    }
  }

  formatValueWithRange(value, postfix, range) {
    if (range.length === 0) {
      return <>{value}{postfix ? <span className={'card-subtitle ml-4'}>{postfix}</span> : null}</>;
    } else {
      const [good, normal, bad] = range;
      if (value < good) {
        return <><small className="border-radius-4 text-white px-8 py-2" style={{backgroundColor: '#6200EA'}}>{value}</small>{postfix ? <span className={'card-subtitle ml-4'}>{postfix}</span> : null}</>
      } else if (value >= good && value < normal) {
        return <><small className="border-radius-4 text-white px-8 py-2" style={{backgroundColor: '#F57C00'}}>{value}</small>{postfix ? <span className={'card-subtitle ml-4'}>{postfix}</span> : null}</>
      } else {
        return <><small className="border-radius-4 bg-error text-white px-8 py-2">{value}</small>{postfix ? <span className={'card-subtitle ml-4'}>{postfix}</span> : null}</>
      }
    }
  }

  formatValueWithRangeReport(value, range) {
    if (range.length === 0) {
      return value;
    } else {
      const [good, normal, bad] = range;
      if (value < good) {
        return <small className="border-radius-4 text-white px-8 py-2" style={{backgroundColor: '#6200EA'}}>{value}</small>;
      } else if (value >= good && value < normal) {
        return <small className="border-radius-4 text-white px-8 py-2" style={{backgroundColor: '#F57C00'}}>{value}</small>;
      } else {
        return <small className="border-radius-4 bg-error text-white px-8 py-2">{value}</small>;
      }
    }
  }

  getColorWithRange(value, range) {
    if (range.length === 0) {
      return '#909090';
    } else {
      const [good, normal, bad] = range;
      if (value < good) {
        return '#6200EA';
      } else if (value >= good && value < normal) {
        return '#F57C00';
      } else {
        return '#f44336';
      }
    }
  }

  formatBadGrade(badGrade) {
    switch (badGrade) {
      case 0: return 'Good';
      case 1: return 'Normal';
      case 2: return 'Bad';
      case 3: return 'Serious';
      default: return 'Unknown'
    }
  }

  getErrorMessage(error: Error) {
    if(error.name === 'APIError') {
      return error?.response?.data?.message ?? 'Something went wrong.';
    } else {
      return error.message ?? 'Something went wrong.';
    }
  }

  getLocation(gpsInfo: string): [number, number] | null {
    if(typeof gpsInfo === 'string') {
      const parts = gpsInfo.split(',');
      if(parts.length === 2) {
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim());
        if(lat && lng) {
          //console.log('hello', [lat, lng]);
          // 네이버맵 전용
          return {lat: lat, lng: lng};
          // return [lat, lng];
        }
      }
      return null;
    }
  }

  isSuperAdmin() {
    const user = Storage.user;
    return user.permission === PERM_SUPER_ADMIN;
  }

  isCompanyManager() {
    const user = Storage.user;
    return user.permission === PERM_COMPANY_ADMIN;
  }

  isSmallScreen() {
    // TODO
    return window?.innerWidth < 1024;
  }

  getAnnotation(datasets, range = []) {
    if (range.length <= 0 || !datasets) return undefined;
    let countGood = 0;
    let countNormal = 0;
    let countBad = 0;
    const [good, normal, bad] = range;
    for (const dataset of datasets) {
      for (const value of dataset.data) {
        if (value >= good) ++countGood;
        if (value >= bad) ++countBad;
        if (value >= normal) ++countNormal;
      }
    }
    const annotation = {
      drawTime: 'afterDatasetsDraw',
      events: ['click'],
      annotations: [
        {
          type: 'line',
          mode: 'horizontal',
          value: good,
          borderColor: '#6200EA',
          borderWidth: 3,
          scaleID: 'y-axis-0',
        },
      ],
    };
    if (countNormal > 0) {
      annotation.annotations.push({
        type: 'line',
        mode: 'horizontal',
        value: normal,
        borderColor: '#F57C00',
        borderWidth: 3,
        scaleID: 'y-axis-0',
      });
    }
    if (countBad > 0) {
      annotation.annotations.push({
        type: 'line',
        mode: 'horizontal',
        value: bad,
        borderColor: '#f44336',
        borderWidth: 3,
        scaleID: 'y-axis-0',
      });
    }
    return annotation;
  }

  downloadPDF(onEnd) {
    const element = document.querySelector('.content');
    //console.log(element);
    html2canvas(element).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const width = element.clientWidth//parseInt(element.style.width.replace('px', ''));
      const height = element.clientHeight//parseInt(element.style.height.replace('px', ''));
      console.log(canvas, width, height, canvas.width, canvas.height);
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [width, height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0);
      pdf.save(this.getReportName());
      onEnd && onEnd();
    });
  }

  setReportInfo(company, branch, name) {
    this.reportInfo = {company, branch, name};
  }

  clearReportInfo() {
    this.reportInfo = {};
  }

  getReportName() {
    const {company, branch, name} = this.reportInfo ?? {};
    const ts = moment().format('YYYYMMDDHHmmss');
    if (company && branch && name) {
      return `report-${name}-${company}-${branch}-${ts}.pdf`;
    } else {
      return `report-${ts}.pdf`;
    }
  }

}

export default new Util();
